import React, { useContext, useState, useEffect } from 'react';
import { Button, TextField, Box, Typography, IconButton, Link, CircularProgress } from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import { useNavigate, useLocation } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import UserApi from '../../../rest-api/user-api';
import DownloadUtils from '../../../utils/download-utils';
import config from '../../../config';
import { designTokens } from '../../../theme';
import { ReactComponent as CetraLogo } from '../../../assets/icons/CetraLogo.svg';
import { ReactComponent as LogoCircle } from '../../../assets/icons/LogoCircle.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/icons/EyeOff.svg';
import { loginStyles, getFullWidthButtonStyle } from './styles/onboard-login.styles';

const OnboardLoginScreen = () => {
    const appStateStore = useContext(StoreContext);
    const { authStore } = useContext(StoreContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [inviteData, setInviteData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { clinicType } = config;

    useEffect(() => {
        document.title = `Cetra for Vets`;
    }, [clinicType]);

    useEffect(() => {
        const fetchInviteData = async () => {
            try {
                const queryParams = new URLSearchParams(location.search);
                const inviteId = queryParams.get('inviteId');
                const senderId = queryParams.get('senderId');

                if (inviteId && senderId) {
                    const inviteData = await UserApi.getClinicianDataMultiuserNoAuth(inviteId);
                    inviteData.inviteId = inviteId;
                    inviteData.senderId = senderId;

                    setInviteData(inviteData);
                    console.warn('Fetched invite data:', inviteData);
                }
            } catch (error) {
                console.error('Error fetching invite data:', error);
            }
        };

        fetchInviteData();
    }, [location.search]);

    const onSubmit = async () => {
        const timestamp = Date.now();
        const dateObject = new Date(timestamp);
        const formattedDate = `${dateObject.getDate()}-${dateObject.getMonth() + 1}-${dateObject.getFullYear()} ${dateObject.getHours()}:${dateObject.getMinutes()}:${dateObject.getSeconds()}`;

        setIsLoading(true);

        try {
            const result = await authStore.signInUser(email, password, async (userId, token) => {
                console.warn('ID Token after authentication:', token);

                await appStateStore.initializeUserStores(userId, token);

                const clinicianDataResponse = await UserApi.getClinicianData(token);
                console.log('[LOGIN] clinicianDataResponse:', clinicianDataResponse);
                console.log(
                    '[LOGIN] clinicianDataResponse.customisedHandouts:',
                    clinicianDataResponse?.customisedHandouts,
                );
                console.log('[LOGIN] clinicianDataResponse.whiteboards:', clinicianDataResponse?.whiteboards);

                if (clinicianDataResponse) {
                    let isMultiuser = false;
                    let userHandouts = clinicianDataResponse.handouts;
                    let inviteId = '';
                    let foundMultiUserData = {};

                    if (clinicianDataResponse.multiuserData && clinicianDataResponse.multiuserData.multiuserUsers) {
                        const foundUser = clinicianDataResponse.multiuserData.multiuserUsers.find(
                            (user) => user.userId === userId,
                        );
                        if (foundUser) {
                            isMultiuser = true;
                            userHandouts = foundUser.handouts || userHandouts;
                            inviteId = foundUser.inviteId || '';
                            foundMultiUserData = foundUser;
                        }
                    }

                    let formattedData = {
                        firstName: foundMultiUserData.firstName || clinicianDataResponse.userData.firstName,
                        lastName: foundMultiUserData.lastName || clinicianDataResponse.userData.lastName,
                        emailAddress: foundMultiUserData.emailAddress || clinicianDataResponse.userData.emailAddress,
                        mobile: foundMultiUserData.mobile || clinicianDataResponse.userData.mobile || '',
                        clinicImageId: '',
                        showLogoInHandouts: clinicianDataResponse.showLogoInHandouts,
                        handouts: JSON.stringify(userHandouts),
                        recentSearches: isMultiuser
                            ? foundMultiUserData.recentSearches || []
                            : clinicianDataResponse.recentSearches || [],
                        inviteId: inviteId,
                        whiteboards: isMultiuser
                            ? foundMultiUserData.whiteboards || []
                            : clinicianDataResponse.whiteboards || [],

                        whiteboardsFavourites: isMultiuser
                            ? foundMultiUserData.whiteboardsFavourites || []
                            : clinicianDataResponse.whiteboardsFavourites || [],

                        customisedHandouts: isMultiuser
                            ? foundMultiUserData.customisedHandouts || []
                            : clinicianDataResponse.customisedHandouts || [],

                        customisedHandoutsFavourites: isMultiuser
                            ? foundMultiUserData.customisedHandoutsFavourites || []
                            : clinicianDataResponse.customisedHandoutsFavourites || [],
                        savedHandouts: isMultiuser
                            ? foundMultiUserData.savedHandouts || []
                            : clinicianDataResponse.savedHandouts || [],
                        savedHandoutsFavourites: isMultiuser
                            ? foundMultiUserData.savedHandoutsFavourites || []
                            : clinicianDataResponse.savedHandoutsFavourites || [],

                        lastUsedColours: isMultiuser
                            ? (foundMultiUserData?.lastUsedColours ??
                              clinicianDataResponse?.lastUsedColours ?? { pencil: [], spray: [] })
                            : (clinicianDataResponse?.lastUsedColours ?? { pencil: [], spray: [] }),
                    };

                    if (clinicianDataResponse.clinicData) {
                        formattedData.clinicName = clinicianDataResponse.clinicData.clinicName;
                        formattedData.clinicNumber = clinicianDataResponse.clinicData.clinicNumber || '';
                        formattedData.clinicSize = clinicianDataResponse.clinicData.clinicSize || '';

                        if (clinicianDataResponse.clinicData.clinicImageId) {
                            const payload = {
                                files: [{ method: 'get', key: clinicianDataResponse.clinicData.clinicImageId }],
                                contentCategory: 'clinic_image',
                            };
                            try {
                                const clinicImageURL = await UserApi.generatePresignedUrls(token, payload);
                                if (clinicImageURL && clinicImageURL.getUrls && clinicImageURL.getUrls.length > 0) {
                                    let clinicImageBase64 = await DownloadUtils.downloadImageFromPresignedUrl(
                                        clinicImageURL.getUrls[0].presignedUrl,
                                    );
                                    formattedData.clinicImageId = clinicImageBase64.base64data;
                                }
                            } catch (presignError) {
                                console.error('Error generating presigned URL:', presignError);
                            }
                        }
                    }

                    if (clinicianDataResponse.subscriptionData) {
                        formattedData.subscriptionData = {
                            ...clinicianDataResponse.subscriptionData,
                            accountType: isMultiuser
                                ? 'Clinic-multiuser'
                                : clinicianDataResponse.subscriptionData.accountType,
                        };
                    }

                    if (appStateStore) {
                        console.warn('attempting to store: ', formattedData, appStateStore);
                        console.log('[LOGIN] formattedData.customisedHandouts:', formattedData.customisedHandouts);
                        console.log('[LOGIN] formattedData.whiteboards:', formattedData.whiteboards);

                        const { userDataStore } = appStateStore;
                        userDataStore.setUserData(formattedData);

                        console.log(
                            '[LOGIN] After setUserData - userDataStore.userData.customisedHandouts:',
                            userDataStore.userData.customisedHandouts,
                        );
                        console.log(
                            '[LOGIN] After setUserData - userDataStore.userData.whiteboards:',
                            userDataStore.userData.whiteboards,
                        );
                    } else {
                        console.error('userDataStore is not defined.');
                    }
                } else {
                    console.error('Clinician data fetch failed.');
                    setErrorMessage('Failed to fetch user data. Please try again.');
                    setIsDialogOpen(true);
                    throw new Error('Failed to fetch user data. Please try again.');
                }
            });

            if (result.success) {
                mixpanel.identify(email);
                mixpanel.people.set({ $email: email });
                mixpanel.track('Login Success', { email: email, time: formattedDate });
            } else {
                mixpanel.track('Login Failure', {
                    email: email,
                    errorMessage: result.message,
                    time: formattedDate,
                });

                setIsDialogOpen(true);
                throw new Error('Please try again with valid details');
            }
        } catch (error) {
            console.error('Error during authentication:', error);
            mixpanel.track('Login Error', { email: email, error: error.message, time: formattedDate });
            if (error.message === 'User is not confirmed.') {
                setErrorMessage('User does not exist - please register');
            } else {
                setErrorMessage(error.message);
            }
            setIsDialogOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            onSubmit();
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    // Use imported styles
    const styles = loginStyles;

    // Invite flow UI
    if (inviteData) {
        return (
            <Box sx={styles.pageContainer}>
                <Box sx={styles.logo}>
                    <Box sx={{ position: 'relative', ...styles.logoSize }}>
                        <Box component={LogoCircle} sx={styles.logoCircle} />
                        <Box component={CetraLogo} sx={styles.logoIcon} />
                    </Box>
                </Box>
                <Box sx={styles.card}>
                    <Typography sx={{ ...styles.title, textAlign: 'left', marginBottom: 2 }}>
                        Join your team at {inviteData.clinicData?.clinicName}!
                    </Typography>
                    <Typography
                        sx={{
                            ...styles.termsText,
                            textAlign: 'left',
                            color: designTokens.colors.textSecondary,
                            marginBottom: 4,
                        }}
                    >
                        {inviteData.senderData?.firstName} invited you to join the{' '}
                        <strong>{inviteData.clinicData?.clinicName}</strong> workspace on Cetra. Create an account with
                        your work email to accept your invite.
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() =>
                            navigate(
                                `/onboard/register?inviteId=${inviteData.inviteId}&senderId=${inviteData.senderId}`,
                            )
                        }
                        className="login-button-secondary"
                        sx={styles.secondaryButton}
                    >
                        Let's get started
                    </Button>
                </Box>

                <ConfirmationDialog
                    open={isDialogOpen}
                    handleClose={() => setIsDialogOpen(false)}
                    onConfirm={() => setIsDialogOpen(false)}
                    title="Login error"
                    message={errorMessage}
                    confirmText="Close"
                    cancelTextReq={false}
                />
            </Box>
        );
    }

    return (
        <Box sx={styles.pageContainer}>
            {/* Logo */}
            <Box sx={styles.logo}>
                <Box sx={{ position: 'relative', ...styles.logoSize }}>
                    <Box component={LogoCircle} sx={styles.logoCircle} />
                    <Box component={CetraLogo} sx={styles.logoIcon} />
                </Box>
            </Box>

            {/* Login Card */}
            <Box sx={styles.card}>
                <Typography component="h1" className="login-title" sx={styles.title}>
                    Welcome to Cetra
                </Typography>

                {/* Email Field */}
                <Box sx={{ marginBottom: 2 }}>
                    <Typography sx={styles.label}>Email</Typography>
                    <TextField
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        onKeyDown={handleKeyDown}
                        sx={styles.input}
                        placeholder=""
                    />
                </Box>

                {/* Password Field */}
                <Box sx={{ marginBottom: 3 }}>
                    <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}
                    >
                        <Typography sx={{ ...styles.label, marginBottom: 0 }}>Password</Typography>
                        <IconButton
                            onClick={handleTogglePasswordVisibility}
                            sx={{ padding: 0 }}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            disableRipple
                        >
                            <EyeOffIcon style={{ width: 19, height: 16, opacity: showPassword ? 0.5 : 1 }} />
                        </IconButton>
                    </Box>
                    <TextField
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        onKeyDown={handleKeyDown}
                        sx={styles.input}
                        placeholder=""
                        inputProps={{ className: 'mp-mask-password' }}
                    />
                </Box>

                {/* Log in Button */}
                <Button
                    variant="contained"
                    fullWidth
                    onClick={onSubmit}
                    disabled={isLoading || !email || !password}
                    className="login-button-primary"
                    sx={styles.primaryButton}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Log in'}
                </Button>

                {/* Terms Text */}
                <Typography className="login-terms-text" sx={styles.termsText}>
                    By continuing, you agree to the{' '}
                    <Link href={config.tsCsUrl} target="_blank" sx={styles.link}>
                        Terms of use
                    </Link>{' '}
                    and{' '}
                    <Link href={config.privacyPolicyUrl} target="_blank" sx={styles.link}>
                        Privacy policy
                    </Link>
                </Typography>

                {/* Forgot Password Link */}
                <Box sx={{ textAlign: 'right', marginTop: { xs: 2, md: 3 } }}>
                    <Link
                        component="button"
                        onClick={() => navigate('/onboard/login/reset-password')}
                        className="login-forgot-password"
                        sx={styles.forgotPasswordLink}
                    >
                        Forget your password?
                    </Link>
                </Box>
            </Box>

            {/* OR Divider */}
            <Box sx={styles.dividerContainer}>
                <Box sx={styles.dividerLine} />
                <Typography className="login-divider-text" sx={styles.dividerText}>
                    OR
                </Typography>
                <Box sx={styles.dividerLine} />
            </Box>

            {/* Create Account Button */}
            <Button
                variant="contained"
                onClick={() => navigate('/onboard/register')}
                className="login-button-secondary"
                sx={getFullWidthButtonStyle(styles.secondaryButton)}
            >
                Create an account
            </Button>

            <ConfirmationDialog
                open={isDialogOpen}
                handleClose={() => setIsDialogOpen(false)}
                onConfirm={() => setIsDialogOpen(false)}
                title="Login error"
                message={errorMessage}
                confirmText="Close"
                cancelTextReq={false}
            />
        </Box>
    );
};

export default OnboardLoginScreen;
